import React, { memo, useReducer, useState } from 'react';
import {
  AdvanceFilter,
  Button,
  Modal,
  Table,
  Text,
} from 'components/src';
import {
  downloadFile,
  routesName,
  useDeleteAdminDeleteChannel,
  useGetAdminGetAllChannelsList,
  useNavigation,
  usePostAdminExportConversationConversationId,
} from 'core';
import { useTranslation } from 'react-i18next';
import { Flex, Space, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CiEdit } from 'react-icons/ci';
import { PiNotebookLight } from 'react-icons/pi';
import {
  handleFiltering,
  handleModal,
  initialState,
  reducer,
  setId,
  updateCurrentPage,
} from './store';
import { RiDeleteBin6Line, RiFileExcel2Line } from 'react-icons/ri';
import { Button as ButtonAnt } from 'antd';

interface Props {}

const ChannelsList = memo<Props>(() => {
  const { _navigate, _navigateWithParams } = useNavigation();
  const { i18n } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    []
  );
  const [{ currentPage, advanceFilter, showModal, id }, dispatch] =
    useReducer(reducer, initialState);
  const { data, isFetching, refetch } = useGetAdminGetAllChannelsList(
    {
      pageNumber: currentPage,
      pageSize: '10',
      channelName: advanceFilter.title,
    }
  );

  const [excelName, setExelName] = useState('');
  const { mutate: excelMutation, isPending: exelLoading } =
    usePostAdminExportConversationConversationId({
      onSuccess: (res) => {
        downloadFile(excelName, res as Blob);
        setExelName('');
      },
    });

  const { mutate, isPending } = useDeleteAdminDeleteChannel({
    onSuccess: (res) => {
      refetch();
    },
  });

  const columns: any = [
    {
      title: i18n.t('id'),
      key: 'id',
      dataIndex: 'id',

      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('channelName'),
      dataIndex: 'title',
      key: 'title',
      render: (text: any) => <Text>{text}</Text>,
    },
    {
      title: i18n.t('administrator'),
      dataIndex: 'administrator',
      key: 'administrator',
      render: (text: any) => <Text>{text?.name_and_family}</Text>,
    },
    {
      title: i18n.t('supervisor'),
      dataIndex: 'supervisor',
      key: 'supervisor',
      render: (text: any) => <Text>{text?.name_and_family}</Text>,
    },
    {
      title: i18n.t('roles'),
      dataIndex: 'roles',
      key: 'roles',
      render: (text: any) =>
        text?.map((item: any) => {
          return <Text>{item.name}-</Text>;
        }),
    },
    {
      title: i18n.t('operation'),
      key: 'operation',
      dataIndex: 'operation',
      render: (_: any, record: any) => (
        <Flex align="center" justify="start">
          <Space>
            <Button
              title={i18n.t('details')}
              customColor="warning"
              onClick={() => {
                _navigateWithParams(routesName.CHANNELDETAIL, {
                  id: record.id,
                });
              }}
            >
              <PiNotebookLight size={20} />
            </Button>
            <Button
              title={i18n.t('edit')}
              onClick={() => {
                _navigateWithParams(routesName.EDITCHANNEL, {
                  id: record.id,
                });
              }}
            >
              <CiEdit size={20} />
            </Button>
            <Button
              title={i18n.t('delete')}
              customColor="danger"
              onClick={() => {
                dispatch(handleModal(true));
                dispatch(setId(record.id));
              }}
              disabled={isPending}
            >
              <RiDeleteBin6Line size={20} />
            </Button>
            <Button
              style={{
                backgroundColor: 'green',
                display: 'flex',
                color: '#fff',
                border: 'none',
              }}
              loading={exelLoading}
              onClick={() => {
                excelMutation({ conversationId: record.id });
                setExelName(record.title);
              }}
            >
              <RiFileExcel2Line size={25} />
            </Button>
          </Space>
        </Flex>
      ),
    },
  ];

  const onAdvanceFilterChange = (filterValues: any) => {
    dispatch(handleFiltering(filterValues));
  };

  const handlePageChange = (current_page: number) => {
    dispatch(updateCurrentPage(current_page));
  };

  const pagination: TablePaginationConfig = {
    current: data?.meta?.current_page,
    pageSize: 10,
    total: data?.data?.totalCount,
    onChange: handlePageChange,
    showSizeChanger: false,
    position: ['bottomRight'],
  };
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    console.log('selectedRowKeys changed: ', newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const isLoading = isFetching;
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <Modal
        text={i18n.t('AreYouSureYouWantToDelete')}
        id={id}
        open={showModal}
        onOk={() => {
          mutate({
            queryParams: {
              conversationId: id,
            },
          });

          dispatch(handleModal(false));
        }}
        onCancel={() => dispatch(handleModal(false))}
      />
      <Button
        type="primary"
        onClick={() => {
          _navigate(routesName.ADDCHANNEL);
        }}
      >
        <PlusOutlined />
        {i18n.t('addChannel')}
      </Button>
      <Table
        rowSelection={rowSelection}
        filterComponent={
          <AdvanceFilter
            setAdvanceFilter={onAdvanceFilterChange}
            titleField={true}
          />
        }
        loading={isLoading}
        {...{ columns }}
        dataSource={data?.data?.conversations}
        pagination={pagination}
      ></Table>
    </>
  );
});
export { ChannelsList };
